import React, {useState, useCallback, useMemo} from "react";
import {useMutation} from "@tanstack/react-query";
import {
    Box,
    Button,
    FormField,
    Input,
    Container,
    SpaceBetween,
    Header,
    ButtonProps,
    Alert,
    Link
} from "@amzn/awsui-components-react-v3";
import {toast} from "react-toastify";
import {getUserAlias} from "src/utils/cookie-helper";
import {DomainTracker} from "src/model/DomainTracker";
import {parse, unparse} from 'papaparse';
import {v1 as uuidv1} from 'uuid';
import {
    BulkStartRecipeExecutionsResult
} from "@amzn/sm-workflow-orchestration-service-js-client/lib/smworkfloworchestrationservicelambda";
import {AppRoutes} from "src/components/common/router-common";
import {downloadSampleFile} from "src/utils/file-download-helper";
import {PageReadme} from "src/components/common/page-readme";

/**
 * Configuration interface for bulk workflow operations (restart/redrive)
 */
export interface BulkWorkflowOperationConfig {
    /** Operation type identifier (e.g., 'restart', 'redrive') */
    operationType: 'restart' | 'redrive';
    /** Display name for the operation (e.g., 'Restart', 'Redrive') */
    operationDisplayName: string;
    /** Client ID for the API call */
    clientId: string;
    /** Recipe ID for the API call */
    recipeId: string;
    /** Program name for the API call */
    program: string;
    /** Maximum number of rows allowed in CSV */
    maxRows: number;
    /** Sample CSV content for download */
    sampleInput: string;
    /** Sample file name for download */
    sampleInputFileName: string;
    /** Instructions for the readme section */
    readmeInstructions: Array<{ title: string; content: string }>;
    /** Column schema for the readme section */
    readmeColumnSchema: Array<{ name: string; required: boolean; description: string }>;
    /** File example data for the readme section */
    fileExample: Array<Record<string, string>>;
    /** Output example data for the readme section */
    outputExample: Array<Record<string, string>>;
    /** Result text for the readme section */
    resultText: { title: string; content: string };
    /** Required columns for CSV validation (in addition to executionId which is always required) */
    requiredColumns?: string[];
}

interface BulkWorkflowOperationProps {
    config: BulkWorkflowOperationConfig;
}

/**
 * Generic component for bulk workflow operations (restart/redrive)
 * This component handles file upload, validation, S3 upload, and API calls
 * for bulk workflow operations.
 */
export function BulkWorkflowOperation({config}: BulkWorkflowOperationProps) {
    const {
        operationType,
        operationDisplayName,
        clientId,
        recipeId,
        program,
        maxRows,
        sampleInput,
        sampleInputFileName,
        readmeInstructions,
        readmeColumnSchema,
        fileExample,
        outputExample,
        resultText,
        requiredColumns = []
    } = config;

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [bulkOperationResult, setBulkOperationResult] = useState<BulkStartRecipeExecutionsResult>({
        successfulRequests: {},
        failedRequests: {}
    });

    const requestsId = uuidv1();

    // Get the appropriate API methods based on operation type
    const getApiMethods = useCallback((dt: DomainTracker) => {
        if (operationType === 'restart') {
            return {
                uploadS3: (blob: Blob, fileName: string, reqId: string) => 
                    dt.bulkRestartUploadS3(blob, fileName, reqId),
                executeOperation: (s3Path: string, cId: string, rId: string, reqId: string, prog: string) => 
                    dt.bulkRestartWorkflowInstance(s3Path, cId, rId, reqId, prog)
            };
        } else {
            return {
                uploadS3: (blob: Blob, fileName: string, reqId: string) => 
                    dt.bulkRedriveUploadS3(blob, fileName, reqId),
                executeOperation: (s3Path: string, cId: string, rId: string, reqId: string, prog: string) => 
                    dt.bulkRedriveWorkflowInstance(s3Path, cId, rId, reqId, prog)
            };
        }
    }, [operationType]);

    const bulkOperationMutation = useMutation({
        mutationFn: async (s3Path: string) => {
            const dt = new DomainTracker();
            const {executeOperation} = getApiMethods(dt);
            return executeOperation(s3Path, clientId, recipeId, requestsId, program);
        },
        onSuccess: (response) => {
            setBulkOperationResult(response);
            const successCount = Object.keys(response.successfulRequests).length;
            const failureCount = Object.keys(response.failedRequests || {}).length;
            if (successCount > 0 && failureCount === 0) {
                toast.success("Request processed successfully");
            } else if (failureCount > 0) {
                toast.error(`Failed to ${operationType} workflows`);
            }
        },
        onError: (error) => {
            setBulkOperationResult(prev => ({
                ...prev,
                failedRequests: {
                    "error": error instanceof Error ? error.message : 'Unknown error occurred'
                }
            }));
            toast.error(`Bulk ${operationType} failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
        },
        onSettled: () => {
            setLoading(false);
        }
    });

    const uploadS3Mutation = useMutation({
        mutationFn: async (blob: Blob) => {
            const dt = new DomainTracker();
            const {uploadS3} = getApiMethods(dt);
            return uploadS3(blob, selectedFile!.name, requestsId);
        },
        onSuccess: async (s3Path) => {
            await bulkOperationMutation.mutateAsync(s3Path);
        },
        onError: (error) => {
            setLoading(false);
            toast.error(`S3 Upload failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
        }
    });

    const handleFileInput = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        setSelectedFile(file || null);
        setBulkOperationResult({successfulRequests: {}, failedRequests: {}});
    }, []);

    const handleDownloadFailedRequests = useCallback(() => {
        if (!bulkOperationResult.failedRequests) return;

        const failedContent = Object.entries(bulkOperationResult.failedRequests).map(([executionId]) => ({
            executionId
        }));

        const csvContent = unparse(failedContent);
        const fileName = `failed-${operationType}s.csv`;
        downloadSampleFile(csvContent, fileName);
    }, [bulkOperationResult.failedRequests, operationType]);

    const handleFileUpload = useCallback(async () => {
        if (!selectedFile) return;

        try {
            setLoading(true);
            parse(selectedFile, {
                header: true,
                skipEmptyLines: true,
                complete: async (parsingResults) => {
                    if (parsingResults.data.length === 0) {
                        setLoading(false);
                        toast.error("CSV File should have at least one row");
                        return;
                    }
                    if (parsingResults.data.length > maxRows) {
                        setLoading(false);
                        toast.error(`Max ${maxRows} rows allowed`)
                        return;
                    }
                    if (!parsingResults.meta.fields?.includes('executionId')) {
                        setLoading(false);
                        toast.error("CSV file must contain 'executionId' column");
                        return;
                    }

                    // Validate executionId uniqueness
                    const seen = new Set<string>();
                    const duplicates = new Set<string>();
                    for (const row of parsingResults.data as Record<string, any>[]) {
                        const id = row.executionId?.trim();
                        if (!id) continue;
                        if (seen.has(id)) duplicates.add(id);
                        else seen.add(id);
                    }
                    if (duplicates.size > 0) {
                        setLoading(false);
                        toast.error(`Duplicate executionId(s) found: ${Array.from(duplicates).slice(0, 5).join(', ')}${duplicates.size > 5 ? ` and ${duplicates.size - 5} more` : ''}`);
                        return;
                    }

                    // Validate additional required columns
                    const missingColumns = requiredColumns.filter(
                        col => !parsingResults.meta.fields?.includes(col)
                    );
                    if (missingColumns.length > 0) {
                        setLoading(false);
                        toast.error(`CSV file must contain required column(s): ${missingColumns.join(', ')}`);
                        return;
                    }

                    // For redrive operations, validate that reDriveStep values are not empty
                    if (operationType === 'redrive' && requiredColumns.includes('reDriveStep')) {
                        const rowsWithEmptyRedriveStep = (parsingResults.data as Record<string, any>[]).filter(
                            (row, index) => !row.reDriveStep || row.reDriveStep.trim() === ''
                        );
                        if (rowsWithEmptyRedriveStep.length > 0) {
                            setLoading(false);
                            toast.error(`'reDriveStep' column cannot have empty values. Found ${rowsWithEmptyRedriveStep.length} row(s) with empty reDriveStep.`);
                            return;
                        }
                    }

                    try {
                        const enrichedData = (parsingResults.data as Record<string, any>[]).map(row => ({
                            ...row,
                            requester: getUserAlias()
                        }));
                        const blob = new Blob([unparse(enrichedData)], {type: 'text/csv'});
                        await uploadS3Mutation.mutateAsync(blob);
                    } catch (error) {
                        setLoading(false);
                        toast.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
                    }
                }
            });
        } catch (err) {
            setLoading(false);
            toast.error(`${err instanceof Error ? err.message : String(err)}`);
        }
    }, [selectedFile, uploadS3Mutation, maxRows]);

    const handleSubmitClick: ButtonProps["onClick"] = useCallback((event) => {
        event.preventDefault();
        if (selectedFile) {
            handleFileUpload();
        }
    }, [selectedFile, handleFileUpload]);

    const handleDownloadSample = useCallback((sampleFileName: string, downloadFileName: string) => {
        try {
            downloadSampleFile(sampleFileName, downloadFileName);
        } catch (error) {
            toast.error(`Failed to download sample file: ${error instanceof Error ? error.message : 'Unknown error occurred'}`);
        }
    }, []);

    const sampleFileLink = useMemo(
        () => (
            <Link
                href={""}
                onFollow={() => handleDownloadSample(sampleInput, sampleInputFileName)}
            >
                Download sample CSV file
            </Link>
        ),
        [handleDownloadSample, sampleInput, sampleInputFileName]
    );

    // Generate past tense for success message
    const operationPastTense = operationType === 'restart' ? 'restarted' : 'redrove';

    return (
        <Box>
            <Box padding={{bottom: "l"}}>
                <Alert
                    visible={Object.entries(bulkOperationResult.successfulRequests).length > 0}
                    data-testid={`bulk-${operationType}-success-alert`}
                    type={"success"}
                    dismissible={true}
                    onDismiss={() => setBulkOperationResult(prev => ({...prev, successfulRequests: {}}))}>
                    <SpaceBetween direction="vertical" size="s">
                        {Object.entries(bulkOperationResult.successfulRequests).map(([key, value]) => (
                            <div key={key}>
                                Successfully {operationPastTense} Workflow Instance. View Instance detail
                                <Link data-testid={"view-execution-details"}
                                      href={AppRoutes.executionDetail(value)}> {value} </Link>
                            </div>
                        ))}
                    </SpaceBetween>
                </Alert>

                {bulkOperationResult.failedRequests &&
                    Object.keys(bulkOperationResult.failedRequests).length > 0 && (
                        <Alert
                            type="error"
                            data-testid={`bulk-${operationType}-error-alert`}
                            dismissible={true}
                            onDismiss={() => setBulkOperationResult(prev => ({...prev, failedRequests: undefined}))}>
                            <SpaceBetween direction="vertical" size="s">
                                {Object.entries(bulkOperationResult.failedRequests).map(([key, value]) => (
                                    <p key={key}>{value}</p>
                                ))}
                                <Button
                                    onClick={handleDownloadFailedRequests}
                                    iconName="download"
                                    data-testid="download-failed-requests-button">
                                    Download Failed Requests
                                </Button>
                            </SpaceBetween>
                        </Alert>
                    )}
            </Box>

            <Container header={<Header variant="h2">Bulk {operationDisplayName} Workflow</Header>}>
                <SpaceBetween direction="vertical" size="l">
                    <form data-testid={`${operationType}-workflow-form`}>
                        <SpaceBetween direction="vertical" size="l">
                            <FormField label="Requester (auto populated)">
                                <Input
                                    value={getUserAlias()}
                                    disabled={true}
                                />
                            </FormField>
                            <FormField
                                label="Input File (CSV)"
                                data-testid="form-field-file-input"
                                description={`Max ${maxRows} records`}
                            >
                                <input
                                    data-testid={`${operationType}-workflow-file-input`}
                                    type="file"
                                    onChange={handleFileInput}
                                    accept=".csv"
                                    disabled={loading}
                                />
                            </FormField>
                            <SpaceBetween direction="horizontal" size="m">
                                <Button
                                    variant="primary"
                                    formAction="submit"
                                    loading={loading}
                                    disabled={!selectedFile || loading}
                                    onClick={handleSubmitClick}
                                    data-testid={`${operationType}-workflow-submit-button`}
                                >
                                    Submit
                                </Button>
                            </SpaceBetween>
                        </SpaceBetween>
                    </form>
                    <PageReadme
                        maxRows={maxRows}
                        instructions={readmeInstructions}
                        columnSchema={readmeColumnSchema}
                        fileExample={fileExample}
                        outputExample={outputExample}
                        resultText={resultText}
                        sampleFileLink={sampleFileLink}
                    />
                </SpaceBetween>
            </Container>
        </Box>
    );
}